import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MarketingLayout, { Seo, Reveal, PageHero, FinalCta } from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { getBillingPlans } from "@/lib/api";
import { Check } from "@phosphor-icons/react";

export default function PublicPricingPage() {
  const navigate = useNavigate();
  const [interval, setInterval] = useState("monthly");
  const [plans, setPlans] = useState([]);
  useEffect(() => { getBillingPlans().then((r) => setPlans(r.plans || [])).catch(() => {}); }, []);
  const priced = useMemo(() => plans.map((p) => ({
    ...p, price: interval === "monthly" ? p.monthly : p.yearly,
    sub: interval === "monthly" ? "/mo" : "/yr", highlight: p.id === "growth",
  })), [plans, interval]);

  return (
    <MarketingLayout>
      <Seo title="Pricing — Vozelto AI Marketing Platform" path="/pricing"
        description="Simple Vozelto pricing: every plan includes all 12 AI employees, the AI Monthly Planner and a 14-day free trial. Cancel anytime, no hidden fees." />
      <PageHero caps="Pricing" title={<>Simple plans that<br /><span className="brand-text">scale with you.</span></>}
        subtitle="Every plan includes the full AI team, the Monthly Planner and the Approval Queue. 14-day free trial · Cancel anytime · No hidden fees.">
        <div className="mt-7 inline-flex items-center rounded-full border border-white/10 bg-[#14141F] p-1" data-testid="pricing-toggle">
          {["monthly", "yearly"].map((iv) => (
            <button key={iv} data-testid={`pricing-${iv}`} onClick={() => setInterval(iv)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${interval === iv ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a] hover:text-white"}`}>
              {iv === "monthly" ? "Monthly" : "Yearly"}
              {iv === "yearly" && <span className="ml-1.5 text-[10px] opacity-80">save ~2mo</span>}
            </button>
          ))}
        </div>
      </PageHero>

      <section className="max-w-6xl mx-auto px-5 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-start">
          {priced.map((p) => (
            <div key={p.id} data-testid={`pricing-card-${p.id}`}
              className={`card-matte p-7 relative ${p.highlight ? "brand-glow border border-[#6B3DFF]/30" : ""}`}>
              {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#6B3DFF] text-white text-[10px] font-bold px-3 py-1 tracking-wide whitespace-nowrap">RECOMMENDED</span>}
              <h3 className="font-head font-semibold text-xl">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-head font-extrabold text-4xl">${p.price}</span>
                <span className="text-[#7a7a7a] mb-1.5 text-sm">{p.sub}</span>
              </div>
              <Button data-testid={`pricing-cta-${p.id}`} onClick={() => navigate(`/signup?plan=${p.id}&interval=${interval}`)}
                className={`mt-5 w-full rounded-full h-11 ${p.highlight ? "bg-[#6B3DFF] hover:bg-[#A56CFF] text-white" : "bg-white/10 hover:bg-white/15 text-white"}`}>
                Start Free Trial
              </Button>
              <ul className="mt-6 space-y-2.5">
                {(p.features || []).map((ft) => (
                  <li key={ft} className="flex items-start gap-2.5 text-sm text-[#cfcfcf]">
                    <Check size={16} className="brand-text mt-0.5 shrink-0" weight="bold" /> {ft}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {priced.length > 0 && (
            <div data-testid="pricing-card-enterprise" className="card-matte p-7 relative">
              <h3 className="font-head font-semibold text-xl">Enterprise</h3>
              <div className="mt-4"><span className="font-head font-extrabold text-3xl">Custom</span></div>
              <Button data-testid="pricing-cta-enterprise" onClick={() => window.open("mailto:info@vozelto.com?subject=Vozelto%20Enterprise", "_blank")}
                className="mt-5 w-full rounded-full h-11 bg-white/10 hover:bg-white/15 text-white">Contact Sales</Button>
              <ul className="mt-6 space-y-2.5">
                {["Unlimited workspaces", "Dedicated onboarding", "Custom integrations", "Priority support & SLA"].map((ft) => (
                  <li key={ft} className="flex items-start gap-2.5 text-sm text-[#cfcfcf]">
                    <Check size={16} className="brand-text mt-0.5 shrink-0" weight="bold" /> {ft}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {priced.length === 0 && <div className="text-[#7a7a7a] text-sm col-span-4 text-center py-10">Loading plans…</div>}
        </div>
        <Reveal className="mt-8 text-center text-sm text-[#7a7a7a]">
          All plans include: 12 AI employees · AI Monthly Planner · Approval Queue · Brand Assets · Facebook & Instagram publishing · Shopify sync · Secure Stripe billing
        </Reveal>
      </section>
      <FinalCta title="Start Free. Decide in 14 Days." sub="Card required for trial, charged $0 today. Cancel in two clicks before day 14 and pay nothing." />
    </MarketingLayout>
  );
}
